import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { useRules } from '../context/RuleContext';
import { validateCarrierId } from '../utils/validator';

const CARRIER_GROUP_NAMES = {
  '1': 'US Market', '2': 'North America', '3': 'Europe + UK',
  '4': 'Southeast Asia + AU', '5': 'US & Canada', '6': 'Western Europe',
  '7': 'Asia Pacific', '8': 'CA/GB/US', '9': 'US Carriers',
  '10': 'European & US Carriers', '11': 'Global Mix', '12': 'US Low Cost',
  '13': 'Finland (Finnair)', '14': 'Premium Carriers',
  '15': 'Full Service Global', '16': 'Extended Global',
};

export default function CarrierGroups() {
  const { carrierGroups, addCarrierGroup, updateCarrierGroup, deleteCarrierGroup, feeRules } = useRules();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null); // null = adding new, otherwise editing
  const [tableNum, setTableNum] = useState('');
  const [carrierInput, setCarrierInput] = useState('');
  const [carriers, setCarriers] = useState([]);
  const [error, setError] = useState('');

  const handleOpenAdd = () => {
    setEditingGroup(null);
    setTableNum('');
    setCarriers([]);
    setError('');
    setDialogOpen(true);
  };

  const handleOpenEdit = (group) => {
    setEditingGroup(group);
    setTableNum(group.tableNum);
    setCarriers([...group.carriers]);
    setError('');
    setDialogOpen(true);
  };

  const handleAddCarrier = () => {
    const val = carrierInput.trim().toUpperCase();
    const err = validateCarrierId(val);
    if (err) { setError(err); return; }
    if (carriers.includes(val)) { setError('Already added'); return; }
    setCarriers([...carriers, val]);
    setCarrierInput('');
    setError('');
  };

  const handleSubmit = () => {
    if (!tableNum) { setError('Table number required'); return; }
    if (carriers.length === 0) { setError('At least one carrier required'); return; }

    if (editingGroup) {
      updateCarrierGroup(editingGroup.tableNum, carriers);
    } else {
      addCarrierGroup({ tableNum, carriers });
    }
    setDialogOpen(false);
  };

  const handleDelete = (tNum) => {
    if (window.confirm(`Delete carrier group #${tNum}?`)) deleteCarrierGroup(tNum);
  };

  const getGroupName = (tableNum) => CARRIER_GROUP_NAMES[tableNum] || `Group ${tableNum}`;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>Carrier Groups ({carrierGroups.length})</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd}>Add Carrier Group</Button>
      </Box>

      {carrierGroups.length === 0 ? (
        <Card><CardContent sx={{ textAlign: 'center', py: 6 }}>
          <Typography color="text.secondary">No carrier groups. Upload an XML file or add one.</Typography>
        </CardContent></Card>
      ) : (
        <Grid container spacing={2}>
          {carrierGroups.map(group => (
            <Grid item xs={12} sm={6} md={4} key={group.tableNum}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="h6" color="primary">Group #{group.tableNum}</Typography>
                    <Box>
                      <IconButton size="small" color="primary" onClick={() => handleOpenEdit(group)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(group.tableNum)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{getGroupName(group.tableNum)}</Typography>
                  <Typography variant="caption" color="text.secondary">Ref: <code>U:{group.tableNum}</code> | {group.carriers.length} carriers</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {group.carriers.map(c => <Chip key={c} label={c} size="small" variant="outlined" color="primary" />)}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingGroup ? `Edit Carrier Group #${editingGroup.tableNum}` : 'Add Carrier Group'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {!editingGroup && (
            <TextField fullWidth label="Table Number" type="number" value={tableNum}
              onChange={(e) => setTableNum(e.target.value)} sx={{ mb: 2, mt: 1 }}
              helperText="Unique ID — referenced as U:N in rules" inputProps={{ min: 1 }} />
          )}

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Carriers ({carriers.length}) {editingGroup && `— Group #${editingGroup.tableNum}: ${getGroupName(editingGroup.tableNum)}`}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField fullWidth size="small" label="IATA Code" value={carrierInput}
              onChange={(e) => setCarrierInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddCarrier(); } }}
              placeholder="e.g. LH, AA, EK" />
            <Button variant="outlined" onClick={handleAddCarrier} sx={{ minWidth: 80 }}>Add</Button>
          </Box>

          {carriers.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {carriers.map(c => (
                <Chip key={c} label={c} color="primary" onDelete={() => setCarriers(carriers.filter(x => x !== c))} />
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>{editingGroup ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
